/* eslint-disable react/prop-types */
import { useEffect } from 'react';

import {
  Navigate, Outlet, useLocation, useParams
} from 'react-router-dom';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { BusinessEntityProvider } from '@zenbusiness/application-commons-business-entity';
import { useEnv } from '@zenbusiness/application-commons-env';
import { ExperimentsIdentity } from '@zenbusiness/application-commons-experiments';
import { ErrorBoundary } from '@zenbusiness/application-commons-logging';
import Empty from '@zenbusiness/zui/Empty';
import PageError from '@zenbusiness/zui/PageError';

import PriceListWrapper from '#client/App/PriceListWrapper';
import { NAME } from '#client/build-env';
import IdentityManager from '#client/components/IdentityManager';
import { login, logout, accountNotFound } from '#client/constants/routes';
import { OrderTrackerStatusProvider } from '#client/hooks/useOrderTrackerStatus';

import ClickstreamWrapper from './ClickstreamWrapper';
import ValidateEntityUuid from './ValidateEntityUuid';

/**
 * Router outlet which validates authentication and sets up the business entity provider.
 * This must be performed within an outlet, because BusinessEntityProvider needs access to react-router,
 * which does not exist at the Providers.js layer.
 * @param {object} props
 * @param {boolean} [props.optional] If false or absent, an unauthenticated user will be forced to login
 * @param {*} props.children
 * @component
 */
export default function Authenticated({ optional, children }) {
  const { businessEntityUuid } = useParams();
  const { accountUuid, authenticated } = useAuthentication();
  const { pathname, search } = useLocation();
  const {
    DEFAULT_PRICE_LIST_UUID
  } = useEnv('root-first');
  useEffect(() => {
    if (optional || authenticated) {
      return;
    }

    // Redirect to the dashboard-ui login page, eventually redirecting back to this page.
    const loginUrl = window.location.origin + login;
    const queryParams = new URLSearchParams();
    queryParams.append('redirectTo', `${pathname}${search}`);
    const finalLoginUrl = `${loginUrl}?${queryParams.toString()}`;
    window.location.assign(finalLoginUrl);
  }, [pathname, search, optional, authenticated]);

  if (!optional && !authenticated) {
    return <Empty unauthenticated />;
  }

  if (!optional && !accountUuid) {
    return (
      <Navigate to={`${logout}?redirectTo=${accountNotFound}`} replace />
    );
  }

  // we put the BE provider here because it needs access to react-router's contexts,
  // which do not exist in PreRouting.
  //
  return (
    <ErrorBoundary
      fallback={<PageError />}
      id={`${NAME}/App/Authenticated`}
    >
      <ValidateEntityUuid>
        <BusinessEntityProvider businessEntityUuid={businessEntityUuid}>
          <OrderTrackerStatusProvider>
            <PriceListWrapper priceListUuid={DEFAULT_PRICE_LIST_UUID}>
              <ClickstreamWrapper>
                <ExperimentsIdentity />
                <IdentityManager />
                {children || <Outlet />}
              </ClickstreamWrapper>
            </PriceListWrapper>
          </OrderTrackerStatusProvider>
        </BusinessEntityProvider>
      </ValidateEntityUuid>
    </ErrorBoundary>
  );
}
